import { invitationRequestSchema, USER_ROLES, type UserRole } from '@beta-assur/shared';
import { useState, type FormEvent } from 'react';
import { ConfirmButton } from '../../components/confirm';
import { Field } from '../../components/page';
import { ActionError, Empty, QueryView } from '../../components/states';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog } from '../../components/ui/dialog';
import { Input, Select } from '../../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table';
import { useApi, useAuth } from '../../lib/auth';
import { instant } from '../../lib/format';
import { useI18n } from '../../lib/i18n';
import { useApiMutation, useApiQuery } from '../../lib/query';
import { useTenant } from '../../lib/referentials';
import type { InvitationResponse, UserList } from '../../lib/types';
import { fieldErrors } from '../portfolio-forms';

// Utilisateurs et rôles (BA-026) : invitation, rôle, désactivation (dernier admin protégé par l'API).

function InviteDialog() {
  const t = useI18n().t;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'manager' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [link, setLink] = useState<string | null>(null);
  const invite = useApiMutation<unknown, InvitationResponse>('POST', '/v1/users/invitations', {
    invalidate: ['/v1/users'],
    onSuccess: (r) =>
      setLink(
        `${window.location.origin}/invitation?token=${encodeURIComponent(r.invitationToken)}`,
      ),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = invitationRequestSchema.safeParse(form);
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    invite.mutate(parsed.data);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setLink(null);
          setForm({ name: '', email: '', role: 'manager' });
        }
      }}
      title={t.settings.invite}
      trigger={<Button size="sm">{t.settings.invite}</Button>}
    >
      {link ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm">{t.settings.invitationLink}</p>
          <Input
            readOnly
            value={link}
            onFocus={(e) => e.target.select()}
            dir="ltr"
            aria-label={t.settings.invitationLink}
          />
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
          <Field label={t.settings.name} error={errors.name}>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Field>
          <Field label={t.settings.email} error={errors.email}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Field>
          <Field label={t.settings.role}>
            <Select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            >
              {USER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t.roles[r]}
                </option>
              ))}
            </Select>
          </Field>
          <ActionError error={invite.error} />
          <div className="flex justify-end">
            <Button type="submit" disabled={invite.isPending}>
              {t.settings.invite}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

export function UsersSection() {
  const { t, language } = useI18n();
  const api = useApi();
  const me = useAuth().state.user;
  const tz = useTenant().data?.timezone;
  const query = useApiQuery<UserList>('/v1/users');
  const [error, setError] = useState<Error | null>(null);

  async function patch(id: string, body: { role?: UserRole; status?: 'active' | 'disabled' }) {
    setError(null);
    try {
      await api.request('PATCH', `/v1/users/${id}`, { body });
      await query.refetch();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.settings.users}</CardTitle>
        <InviteDialog />
      </CardHeader>
      <div className="px-4 pt-2">
        <ActionError error={error} />
      </div>
      <QueryView query={query}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>{t.settings.name}</TH>
                  <TH>{t.settings.email}</TH>
                  <TH>{t.settings.role}</TH>
                  <TH>{t.settings.status}</TH>
                  <TH>{t.settings.totp}</TH>
                  <TH>{t.settings.lastLogin}</TH>
                  <TH>
                    <span className="sr-only">{t.common.actions}</span>
                  </TH>
                </tr>
              </THead>
              <TBody>
                {data.items.map((u) => (
                  <TR key={u.id}>
                    <TD>{u.name}</TD>
                    <TD>{u.email}</TD>
                    <TD>
                      <Select
                        aria-label={`${t.settings.role} ${u.name}`}
                        className="w-40"
                        value={u.role}
                        disabled={u.id === me?.id}
                        onChange={(e) =>
                          void patch(u.id, { role: e.target.value as UserRole }).catch(
                            () => undefined,
                          )
                        }
                      >
                        {USER_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {t.roles[r]}
                          </option>
                        ))}
                      </Select>
                    </TD>
                    <TD>
                      <Badge
                        tone={
                          u.status === 'active'
                            ? 'success'
                            : u.status === 'disabled'
                              ? 'neutral'
                              : 'warning'
                        }
                      >
                        {t.settings.userStatuses[u.status]}
                      </Badge>
                    </TD>
                    <TD>{u.totpEnabled ? t.settings.totpEnabled : t.settings.totpDisabled}</TD>
                    <TD>{instant(u.lastLoginAt, tz, language) || '-'}</TD>
                    <TD>
                      {u.id === me?.id || u.status === 'invited' ? null : u.status ===
                        'disabled' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            void patch(u.id, { status: 'active' }).catch(() => undefined)
                          }
                        >
                          {t.settings.enable}
                        </Button>
                      ) : (
                        <ConfirmButton
                          label={t.settings.disable}
                          title={t.settings.disable}
                          message={t.settings.disableConfirm}
                          variant="destructive"
                          onConfirm={() => patch(u.id, { status: 'disabled' })}
                        />
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
    </Card>
  );
}
